/* player.tsx */
import AnalyticsInstance from '@shared/utils/analytics';
import Container from '../atoms/container/playerContainer';
import Focus from '../../../shared/types/enums/focus';
import Modals from '../../../shared/types/enums/modals';
import ParentalUtils from '../../../shared/utils/parental';
import Pilot from '../sections/pilot/pilot';
import Player from '../elements/mediaPlayer/playerSelector';
import Routes from '../../../shared/types/enums/routes';
import Spinner from '../elements/spinner/spinner';
import sendBookmark from '../../../shared/api/bookmark/request';
import useBackKeyboard from '../../../shared/components/utils/hooks/useBackKeyboard';
import useKeyboard from '../../../shared/components/utils/hooks/useKeyboard';
import usePauseAds from '@shared/components/utils/hooks/usePauseAds';
import usePlayAds from '@shared/components/utils/hooks/usePlayAds';
import { Coordinates } from '@shared/utils/floatingMenu';
import { PlayerTemplateProps as Props } from '../../../shared/types/props/player';
import { PlayerState, TimeShift } from '../../../shared/types/enums/player';
import { magicRemoteSelector } from '../../../shared/store/slices/magicRemote.slice';
import { setParams } from '@shared/store/slices/router.slice';
import { store } from '../../../shared/store/store';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { useEffect, useState, useCallback, useRef } from 'react';
import { userSelector } from '../../../shared/store/slices/user.slice';
import {
  addKeepWatchingItem,
  updateKeepWatchingItem,
} from '../../../shared/store/slices/keepWatching.slice';
import {
  clearFocus,
  focusSelector,
  setFocus,
} from '../../../shared/store/slices/focus.slice';
import {
  clearModal,
  modalSelector,
  setModal,
  setModalParams,
} from '../../../shared/store/slices/modal.slice';
import {
  closeFloatingMenu,
  openFloatingMenu,
} from '../../../shared/store/thunks/floatingMenu.thunk';
import {
  mediaSelector,
  toggle as togglePlayer,
  pause,
  play,
  reset,
  seek,
  setRange,
  setVod,
} from '../../../shared/store/slices/media.slice';
import {
  playerSelector,
  resetPlayer,
  setNextEpisode,
  setPilot,
} from '../../../shared/store/slices/player.slice';

const PILOT_DISPLAY_TIME = 10 * 1000;

/**
 * Main player template component for VOD content playback.
 * Manages player state, keyboard interactions, floating menu, pilot overlay, and pause ads.
 * Handles bookmarking, keep watching functionality, and parental controls.
 *
 * @param {Props} props - Player template props containing the asset to play
 * @returns {JSX.Element} Player container with media player and pilot overlay
 */
export default function TPlayer({ asset }: Props) {
  const dispatch = useAppDispatch();
  const { focus } = useAppSelector(focusSelector);
  const { enabled } = useAppSelector(magicRemoteSelector);
  const { error, ready, state, position } = useAppSelector(mediaSelector);
  const { current: modal } = useAppSelector(modalSelector);
  const { nextEpisode, pilot } = useAppSelector(playerSelector);
  const { parental } = useAppSelector(userSelector);

  const [allowPlayback, setAllowPlayback] = useState(false);

  const mediaPositionRef = useRef(0);
  if (state !== PlayerState.IDLE) {
    mediaPositionRef.current = position;
  }
  const assetRef = useRef(asset);
  const focusRef = useRef<number | (string | number)[] | null>();
  const modalRef = useRef<Modals | null>();
  const stateRef = useRef<PlayerState>();
  const enabledRef = useRef<boolean>();
  const errorRef = useRef(error);
  const nextEpisodeRef = useRef(nextEpisode);
  const pilotRef = useRef<boolean>();
  const pilotTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const toggledRef = useRef<boolean>(false);

  assetRef.current = asset;
  enabledRef.current = enabled;
  errorRef.current = error;
  focusRef.current = focus;
  nextEpisodeRef.current = nextEpisode;
  pilotRef.current = pilot;
  stateRef.current = state;

  // UI handlers

  const toggleFloatingMenu = useCallback(
    (open = true) => {
      const { media } = store.getState();
      if (
        open &&
        asset &&
        media.state !== PlayerState.IDLE &&
        media.state !== PlayerState.WAITING
      ) {
        const { router } = store.getState();
        const route = {
          asset: asset,
          position: Coordinates.ZAPPER,
          route: Routes.PLAYER,
        };
        const params = router.params.current as {
          content: { keepWatchingMark: number };
        };
        if (params?.content)
          params.content.keepWatchingMark = media.currentTime;
        const stackParams = {
          ...params,
        };
        dispatch(setParams(stackParams));
        dispatch(openFloatingMenu(route));
      } else {
        dispatch(closeFloatingMenu());
      }
    },
    [asset]
  );

  const togglePilot = useCallback((display = true, closeModal = true) => {
    if (
      closeModal &&
      modalRef.current &&
      modalRef.current !== Modals.PARENTAL_PIN_GENERIC &&
      !errorRef.current.code
    ) {
      dispatch(clearModal());
    }
    clearTimeout(pilotTimeoutRef.current);
    if (display) {
      pilotTimeoutRef.current = setTimeout(() => {
        const shouldDisplay =
          focusRef.current === Focus.FLOATING_MENU ||
          stateRef.current === PlayerState.SEEKING ||
          stateRef.current === PlayerState.PAUSE;
        if (shouldDisplay) {
          togglePilot();
        } else {
          dispatch(setPilot(false));
        }
      }, PILOT_DISPLAY_TIME);
    } else {
      dispatch(setPilot(false));
      dispatch(setNextEpisode({ display: false }));
    }
    dispatch(setPilot(display));
  }, []);

  // Event handlers

  const onMouseMove = useCallback(() => {
    togglePilot();
  }, []);

  // Effect hooks

  useEffect(() => {
    // Dispatch parental pin modal
    if (
      modal === null &&
      parental?.pin &&
      parental?.enabled &&
      ParentalUtils.isTimeRestricted(parental?.timeFrames) &&
      ParentalUtils.isAgeRestricted(asset?.ageCode) &&
      modalRef.current !== Modals.PARENTAL_PIN_GENERIC
    ) {
      modalRef.current = Modals.PARENTAL_PIN_GENERIC;
      store.dispatch(setModalParams({ pop: true }));
      store.dispatch(setModal(Modals.PARENTAL_PIN_GENERIC));
    } else if (!modal) {
      const allowPlayback =
        modal !== null
          ? ![
              Modals.ACTIVATE_ZAPPER,
              Modals.ANONYMOUS_ZAPPER,
              Modals.PARENTAL_PIN_GENERIC,
              Modals.PLAYER_ERROR,
            ].includes(modal)
          : true;
      setAllowPlayback(allowPlayback);
    }
  }, [asset, modal, parental]);

  useEffect(() => {
    const send = (forcePosition?: number) => {
      const { media } = store.getState();
      if (asset?.eventId) {
        const mark = forcePosition || media.position;
        sendBookmark(asset?.eventId, mark);
        dispatch(
          updateKeepWatchingItem({
            id: asset.eventId,
            mark,
          })
        );
      }
    };

    return () => {
      const { keepWatching } = store.getState();
      clearTimeout(pilotTimeoutRef.current);
      if (keepWatching.data.find((item) => asset?.eventId == item.id)) {
        send(mediaPositionRef.current);
      } else {
        if (asset?.eventId) {
          dispatch(
            addKeepWatchingItem({
              id: asset?.eventId,
              mark: mediaPositionRef.current,
            })
          );
          send(mediaPositionRef.current);
        }
      }
    };
  }, []);

  useEffect(() => {
    dispatch(reset());
    dispatch(setVod());
    dispatch(setFocus(Focus.PLAYER));
    dispatch(
      setRange({
        bookmarks: {
          start: assetRef.current?.startBookmark,
          end: assetRef.current?.endBookmark,
        },
      })
    );
    return () => {
      dispatch(closeFloatingMenu());
      dispatch(clearFocus());
      dispatch(clearModal());
      dispatch(resetPlayer());
      dispatch(reset());
    };
  }, []);

  useEffect(() => {
    AnalyticsInstance.createPlaybackSession(asset?.channel, asset);
  }, [asset]);

  // Keyboard hook

  useBackKeyboard({
    callback: () => {
      const { ads } = store.getState();
      if (ads.pauseAd) return;
      if (
        !nextEpisodeRef.current.display &&
        (!pilotRef.current || enabledRef.current)
      ) {
        dispatch(setModal(Modals.CLOSE_PLAYER));
      }
      togglePilot(false);
    },
  });

  const keyDownHandler = (key: string) => {
    // Play ad flow key handling
    const { ads } = store.getState();
    if (ads.playAd) return;

    // Normal flow key handlong
    switch (key) {
      case 'UP':
        if (
          !modalRef.current &&
          stateRef.current !== PlayerState.SEEKING &&
          nextEpisodeRef.current.enabled
        ) {
          dispatch(setFocus(Focus.NEXT_EPISODE));
        }
        break;
      case 'REWIND':
      case 'LEFT':
        dispatch(seek(TimeShift.BACKWARDS));
        break;
      case 'FASTFORWARD':
      case 'RIGHT':
        dispatch(seek(TimeShift.FORWARD));
        break;
      case 'PAUSE':
        dispatch(pause());
        break;
      case 'PLAY':
        dispatch(play());
        break;
      case 'PLAY_PAUSE':
      case 'SELECT':
        if (!pilotRef.current) {
          togglePilot();
          toggledRef.current = true;
        }
        break;
    }
  };

  useKeyboard({
    focus: Focus.PLAYER,
    keyDownHandler,
    keyPressHandler() {
      const { ads } = store.getState();
      if (ads?.playAd) return;
      toggleFloatingMenu();
    },
    keyUpHandler: (key, pressed) => {
      // Play ad flow key handling
      const { ads } = store.getState();
      if (ads.playAd) return;

      // Normal flow key handling
      switch (key) {
        case 'BACK':
          break;
        case 'SELECT':
          if (!pressed && pilotRef.current && !toggledRef.current) {
            dispatch(togglePlayer());
          }
          toggledRef.current = false;
          break;
        default:
          !pilotRef.current && togglePilot();
      }
    },
  });

  // Ads hooks

  usePauseAds({
    asset,
    keyHandler: keyDownHandler,
    togglePilot,
    useKeyboard: useKeyboard,
  });

  usePlayAds({
    asset,
    togglePilot,
  });

  // Return

  return (
    <Container onMouseMove={onMouseMove}>
      {allowPlayback && !ready && !error.code ? (
        <Spinner />
      ) : (
        <Pilot vod={asset} />
      )}
      {allowPlayback ? <Player asset={asset} /> : null}
    </Container>
  );
}
